/**
 * Expandable clinical note card with assignment, lifecycle, and audit controls.
 */

import { FileWarning, Paperclip } from "lucide-react";
import { useState } from "react";
import type {
  ClinicalNoteResponse,
  NoteAssigneeResponse,
  UpdateNoteRequest,
} from "../../../api/clinicalTrialsApiTypes";
import type { RichTextDocument } from "../../../types/notes";
import { NoteActivityThread } from "./NoteActivityThread";
import { RichTextContent } from "./RichTextContent";
import { RichTextEditor } from "./RichTextEditor";

interface NoteCardProps {
  note: ClinicalNoteResponse;
  assignees: NoteAssigneeResponse[];
  isExpanded: boolean;
  isSaving: boolean;
  onToggle: () => void;
  onUpdate: (request: UpdateNoteRequest) => Promise<void>;
  onDelete: () => Promise<void>;
  onReply: (body: RichTextDocument) => Promise<void>;
  onUpdateReply: (activityId: string, body: RichTextDocument) => Promise<void>;
  onDeleteReply: (activityId: string) => Promise<void>;
  onCommand: (command: "resolve" | "reopen" | "unread") => Promise<void>;
}

/** Format a note date for compact display. */
function shortDate(value?: string | null): string {
  return value
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
      }).format(new Date(value))
    : "";
}

/**
 * Render one note summary and its expanded content/actions.
 *
 * @param props - Note data, directory, state, and mutation callbacks
 * @returns Expandable note card
 */
export function NoteCard({
  note,
  assignees,
  isExpanded,
  isSaving,
  onToggle,
  onUpdate,
  onDelete,
  onReply,
  onUpdateReply,
  onDeleteReply,
  onCommand,
}: NoteCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body);
  const statusLabel =
    note.status === "resolved"
      ? "Resolved"
      : note.is_overdue
        ? "Overdue"
        : "Open";
  const statusColor =
    note.status === "resolved"
      ? "border-l-statusGreen-active"
      : note.is_overdue
        ? "border-l-statusRed-text"
        : "border-l-amber-500";
  const statusBadge =
    note.status === "resolved"
      ? "bg-statusGreen-bg text-statusGreen-text"
      : note.is_overdue
        ? "bg-statusRed-bg text-statusRed-text"
        : "bg-amber-100 text-amber-800";
  return (
    <article
      className={`overflow-hidden rounded-lg border border-uiGray-200 border-l-4 ${statusColor} bg-white shadow-sm ${isExpanded ? "ring-2 ring-blue-100" : ""}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`w-full px-3 py-2.5 text-left ${isExpanded ? "bg-blue-50" : "hover:bg-uiGray-50"}`}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="truncate text-xs text-primaryGray-8">
            {note.creator.display_name || note.creator.email || "Unknown"} ·{" "}
            {shortDate(note.updated_at)}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusBadge}`}
          >
            {statusLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {note.is_unread && (
            <span className="h-2 w-2 shrink-0 rounded-full bg-statusBlue-text" />
          )}
          <p className="truncate text-sm font-semibold">{note.title}</p>
        </div>
        <div className="mt-1 flex flex-wrap gap-2 text-xs text-primaryGray-8">
          {note.assignee && (
            <span>→ {note.assignee.display_name || note.assignee.email}</span>
          )}
          {note.due_date && (
            <span
              className={
                note.is_overdue
                  ? "font-semibold text-statusRed-text"
                  : "text-amber-700"
              }
            >
              {note.is_overdue ? "Overdue" : "Due"} {shortDate(note.due_date)}
            </span>
          )}
        </div>
      </button>
      {isExpanded && (
        <div>
          <div className="border-t border-blue-100 px-3 py-3">
            {note.source_document_id && (
              <div
                className={`mb-3 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs ${note.source_available ? "bg-uiGray-100" : "bg-statusRed-bg text-statusRed-text"}`}
              >
                {note.source_available ? (
                  <Paperclip size={12} />
                ) : (
                  <FileWarning size={12} />
                )}
                {note.source_available
                  ? note.source_document_name || note.source_document_type
                  : "Source document unavailable"}
              </div>
            )}
            {isEditing ? (
              <div className="space-y-2">
                <input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  aria-label="Edit note title"
                  className="w-full rounded border px-2 py-1 text-sm font-semibold"
                />
                <RichTextEditor
                  value={body}
                  onChange={setBody}
                  placeholder="Note body"
                  ariaLabel="Edit note body"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="btn btn-outline px-2 py-1 text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={async () => {
                      await onUpdate({ title: title.trim(), body });
                      setIsEditing(false);
                    }}
                    className="btn bg-statusBlue-text px-2 py-1 text-xs text-white"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <RichTextContent
                document={note.body}
                className="text-sm leading-6"
              />
            )}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <select
                aria-label="Change assignee"
                value={note.assignee?.user_id ?? ""}
                onChange={(event) =>
                  onUpdate({ assignee_id: event.target.value || null })
                }
                className="rounded border px-2 py-1 text-xs"
              >
                <option value="">Unassigned</option>
                {assignees.map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.display_name || member.email}
                  </option>
                ))}
              </select>
              <input
                type="date"
                aria-label="Change due date"
                value={note.due_date?.slice(0, 10) ?? ""}
                onChange={(event) =>
                  onUpdate({ due_date: event.target.value || null })
                }
                className="rounded border px-2 py-1 text-xs"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="font-medium text-statusBlue-darkText"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() =>
                  onCommand(note.status === "resolved" ? "reopen" : "resolve")
                }
                className="font-medium text-statusGreen-text"
              >
                {note.status === "resolved" ? "Reopen" : "Resolve"}
              </button>
              <button
                type="button"
                onClick={() => onCommand("unread")}
                className="font-medium text-primaryGray-7"
              >
                Mark unread
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm("Delete this note?")) void onDelete();
                }}
                className="font-medium text-statusRed-text"
              >
                Delete
              </button>
            </div>
          </div>
          <NoteActivityThread
            activities={note.activities}
            canReply={note.status === "open"}
            isSaving={isSaving}
            onReply={onReply}
            onUpdateReply={onUpdateReply}
            onDeleteReply={onDeleteReply}
          />
        </div>
      )}
    </article>
  );
}
